import { useState, useCallback, useEffect } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

type SmsProvider = "sinch" | "telnyx";
interface SmsFormData { provider: SmsProvider; apiKey: string; apiSecret: string; sendingNumber: string }
interface SmsComplianceSummary {
  optedInContacts: number;
  optedOutContacts: number;
  totalConsentRecords: number;
  grantedCount: number;
  withdrawnCount: number;
  lastConsentAt: string | null;
}
interface SmsProvisioningSummary {
  configured: boolean;
  primaryProvider: string | null;
  sendingNumber: string | null;
  isEnabled: boolean;
  isValidated: boolean;
  lastValidatedAt: string | null;
  lastErrorMessage: string | null;
  provisionedNumbers: Array<{
    phoneNumber: string;
    provider: string;
    numberType: string;
    country: string;
    status: string;
    healthScore: number;
  }>;
  tenDlc: {
    brandStatus: string;
    brandVettingScore: number | null;
    campaignStatus: string;
    campaignThroughputLimit: number | null;
  };
  keywordAutomation: {
    stopKeywords: string[];
    startKeywords: string[];
    source: string;
  };
}

const STEP_COUNT = 4;
const PROVIDERS: { value: SmsProvider; label: string; desc: string }[] = [
  { value: "sinch", label: "Sinch", desc: "Global SMS provider with high deliverability" },
  { value: "telnyx", label: "Telnyx", desc: "Developer-friendly with competitive pricing" },
];
const INPUT_CLS = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
const BTN_PRI = "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50";
const BTN_SEC = "rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50";
const SUCCESS_CLS = "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
const ERROR_CLS = "border-destructive/30 bg-destructive/10 text-destructive";

function extractError(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: STEP_COUNT }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${i < current ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" : i === current ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {i < current ? "\u2713" : i + 1}
          </div>
          {i < STEP_COUNT - 1 && <div className={`h-px w-8 ${i < current ? "bg-[var(--color-success)]" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function SmsConfigWizard() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<SmsFormData>({ provider: "sinch", apiKey: "", apiSecret: "", sendingNumber: "" });
  const [compliance, setCompliance] = useState<SmsComplianceSummary | null>(null);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [provisioning, setProvisioning] = useState<SmsProvisioningSummary | null>(null);
  const [provisioningError, setProvisioningError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const canAdvance = step === 0 || (step === 1 && form.apiKey.length > 0 && form.sendingNumber.length > 0) || (step === 2 && testResult?.ok) || step === 3;

  const handleTest = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await apiFetch<{ valid: boolean; error?: string }>("/api/messaging/sms/validate", {
        method: "POST",
        body: JSON.stringify({
          provider: form.provider,
          apiKey: form.apiKey,
          apiSecret: form.apiSecret || undefined,
          phoneNumber: form.sendingNumber,
        }),
      });
      if (!result.valid) {
        setTestResult({ ok: false, message: result.error ?? "Credential validation failed" });
        return;
      }
      setTestResult({ ok: true, message: "Credentials verified successfully" });
    } catch (err: unknown) {
      setTestResult({ ok: false, message: extractError(err, "Connection test failed") });
    } finally { setIsTesting(false); }
  }, [form]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveResult(null);
    try {
      await apiFetch("/api/messaging/sms/config", {
        method: "PUT",
        body: JSON.stringify({
          primary: {
            provider: form.provider,
            apiKey: form.apiKey,
            apiSecret: form.apiSecret || undefined,
            phoneNumber: form.sendingNumber,
          },
        }),
      });
      setSaveResult("Configuration saved successfully");
    } catch (err: unknown) {
      setSaveResult(extractError(err, "Configuration save failed"));
    } finally { setIsSaving(false); }
  }, [form]);

  useEffect(() => {
    let isCancelled = false;

    async function loadCompliance() {
      try {
        const result = await apiFetch<{ data: SmsComplianceSummary }>("/api/messaging/sms/compliance");
        if (isCancelled) return;
        setCompliance(result.data);
        setComplianceError(null);
      } catch (err: unknown) {
        if (isCancelled) return;
        setCompliance(null);
        setComplianceError(extractError(err, "Could not load SMS compliance summary"));
      }
    }

    async function loadProvisioning() {
      try {
        const result = await apiFetch<{ data: SmsProvisioningSummary }>("/api/messaging/sms/provisioning");
        if (isCancelled) return;
        setProvisioning(result.data);
        setProvisioningError(null);
      } catch (err: unknown) {
        if (isCancelled) return;
        setProvisioning(null);
        setProvisioningError(extractError(err, "Could not load SMS provisioning summary"));
      }
    }

    void loadCompliance();
    void loadProvisioning();
    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">SMS Configuration</h2>
        <p className="mt-1 text-sm text-muted-foreground">Set up your SMS provider in a few steps</p>
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium text-foreground">TCPA compliance summary</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Live summary from stored SMS opt-in/out preferences and append-only consent records.
            </p>
          </div>
          {compliance?.lastConsentAt ? (
            <span className="text-xs text-muted-foreground">
              Last activity {new Date(compliance.lastConsentAt).toLocaleDateString()}
            </span>
          ) : null}
        </div>

        {complianceError ? (
          <p className="mt-3 text-sm text-destructive">{complianceError}</p>
        ) : compliance ? (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <ComplianceMetric label="Opted in" value={compliance.optedInContacts} />
            <ComplianceMetric label="Opted out" value={compliance.optedOutContacts} />
            <ComplianceMetric label="Consent records" value={compliance.totalConsentRecords} />
            <ComplianceMetric label="Granted" value={compliance.grantedCount} />
            <ComplianceMetric label="Withdrawn" value={compliance.withdrawnCount} />
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">Loading compliance summary…</p>
        )}
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium text-foreground">Number provisioning</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Live sending-number registry, 10DLC readiness, and inbound keyword automation.
            </p>
          </div>
          {provisioning?.configured ? (
            <span className={provisioning.isValidated ? `rounded-full px-2 py-1 text-xs ${SUCCESS_CLS}` : `rounded-full px-2 py-1 text-xs ${ERROR_CLS}`}>
              {provisioning.isValidated ? "Validated" : "Needs review"}
            </span>
          ) : null}
        </div>

        {provisioningError ? (
          <p className="mt-3 text-sm text-destructive">{provisioningError}</p>
        ) : provisioning ? (
          <div className="mt-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <ProvisioningMetric label="Primary provider" value={provisioning.primaryProvider ?? "Not configured"} />
              <ProvisioningMetric label="Sending number" value={provisioning.sendingNumber ?? "Not provisioned"} />
              <ProvisioningMetric label="10DLC brand" value={provisioning.tenDlc.brandStatus} />
              <ProvisioningMetric label="10DLC campaign" value={provisioning.tenDlc.campaignStatus} />
            </div>

            {provisioning.provisionedNumbers.length > 0 ? (
              <div className="rounded-md border border-border bg-background p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Provisioned numbers</p>
                <div className="mt-2 space-y-2">
                  {provisioning.provisionedNumbers.map((number) => (
                    <div key={`${number.provider}-${number.phoneNumber}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-foreground">{number.phoneNumber}</span>
                      <span className="text-xs text-muted-foreground">
                        {number.provider} · {number.numberType.replace(/_/g, " ")} · {number.status} · health {number.healthScore}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
                No sending numbers have been registered in the tenant phone-number registry yet.
              </p>
            )}

            <div className="rounded-md border border-border bg-background p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Keyword lifecycle automation</p>
              <p className="mt-2 text-sm text-foreground">
                STOP keywords opt contacts out automatically; START keywords re-grant SMS consent.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                STOP: {provisioning.keywordAutomation.stopKeywords.join(", ")} · START: {provisioning.keywordAutomation.startKeywords.join(", ")}
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">Loading number provisioning…</p>
        )}
      </div>

      <StepIndicator current={step} />

      {step === 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">Select Provider</h3>
          {PROVIDERS.map((p) => (
            <label key={p.value} className={`flex cursor-pointer items-center gap-3 rounded-md border p-4 transition-colors ${form.provider === p.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
              <input type="radio" name="sms-provider" value={p.value} checked={form.provider === p.value} onChange={() => setForm({ ...form, provider: p.value })} className="accent-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">{p.label}</p>
                <p className="text-xs text-muted-foreground">{p.desc}</p>
              </div>
            </label>
          ))}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Enter Credentials</h3>
          <div>
            <label className="block text-sm font-medium text-foreground">API Key</label>
            <input type="password" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} placeholder="Enter your API key" className={`mt-1 ${INPUT_CLS}`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">API Secret</label>
            <input type="password" value={form.apiSecret} onChange={(e) => setForm({ ...form, apiSecret: e.target.value })} placeholder="Enter your API secret" className={`mt-1 ${INPUT_CLS}`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Sending Number (E.164)</label>
            <input type="text" value={form.sendingNumber} onChange={(e) => setForm({ ...form, sendingNumber: e.target.value })} placeholder="+1234567890" className={`mt-1 ${INPUT_CLS}`} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Test Connection</h3>
          <p className="text-sm text-muted-foreground">Validate your provider credentials and sending number before saving.</p>
          <button onClick={handleTest} disabled={isTesting} className={BTN_SEC}>{isTesting ? "Validating..." : "Validate Credentials"}</button>
          {testResult && <div className={`rounded-md border p-3 text-sm ${testResult.ok ? SUCCESS_CLS : ERROR_CLS}`}>{testResult.message}</div>}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Save Configuration</h3>
          <div className="rounded-md border border-border bg-muted/30 p-4 text-sm">
            <p className="text-foreground"><span className="font-medium">Provider:</span> <span className="capitalize">{form.provider}</span></p>
            <p className="mt-1 text-foreground"><span className="font-medium">Sending Number:</span> {form.sendingNumber}</p>
          </div>
          <button onClick={handleSave} disabled={isSaving} className={BTN_PRI}>{isSaving ? "Saving..." : "Save Configuration"}</button>
          {saveResult && <div className={`rounded-md border p-3 text-sm ${saveResult.includes("successfully") ? SUCCESS_CLS : ERROR_CLS}`}>{saveResult}</div>}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50">Back</button>
        {step < STEP_COUNT - 1 && (
          <button onClick={() => setStep((s) => Math.min(STEP_COUNT - 1, s + 1))} disabled={!canAdvance} className={BTN_PRI}>Next</button>
        )}
      </div>
    </div>
  );
}

function ComplianceMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </p>
    </div>
  );
}

function ProvisioningMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}
